/**
 * AnimatedMonkey — calibrated rig for the Zporti mascot.
 *
 * The monkey is assembled from five separated PNG parts layered on a virtual 1024×1024
 * stage; each part's transform is pre-flattened into an absolute rect so there is no
 * transform-origin ambiguity for static placement.
 *
 * The face uses the original head (`head.png`, eyes baked in) with the original tan lids
 * for a clean full blink. A short-lived "v2" experiment split the head into an upright
 * empty-socket render + a separate darting-pupils layer; it was reverted (pupils looked
 * mis-sized and the big-eye blink read badly), but the idle-personality motions added
 * alongside it were kept. Idle "soul" now comes from small, de-synchronised, non-metronomic
 * motions: breathe (whole body), a slow head sway on the neck, irregular blinks (incl. an
 * occasional double), and a one-shot-style wave whose burst the head leans into.
 *
 * No Lottie / no Rive — plain built-in RN Animated transforms (renders in Storybook/CI).
 */

import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets';

/** Virtual stage the rig was calibrated in. All part rects are in these units. */
export const MONKEY_STAGE = 1024;

/**
 * Pivot point in the part's own 1024×1024 source-image units. Pivots are NOT expressed as
 * `transformOrigin` — native Android ignores the origin once `Animated` starts replacing the
 * `transform` each frame (web honours it, which made the rig look right only on web). Instead
 * each pivot becomes an explicit translate→transform→translate-back sandwich via
 * {@link pivotDelta} — identical math, honoured on every platform.
 */
export interface MonkeyPivot {
  readonly x: number;
  readonly y: number;
}

/**
 * Offset from a box's centre to its pivot, in box px. RN transforms pivot about the centre,
 * so `[translate(+d), …ops, translate(−d)]` reproduces a transform about the pivot exactly.
 */
export const pivotDelta = (pivot: MonkeyPivot, boxSize: number): { dx: number; dy: number } => ({
  dx: (pivot.x / MONKEY_STAGE - 0.5) * boxSize,
  dy: (pivot.y / MONKEY_STAGE - 0.5) * boxSize,
});

export interface MonkeyPartLayout {
  readonly src: number; // require()'d bundled asset (primary source)
  readonly cdn: string; // CDN URL fallback if the bundled asset fails to load (iOS prod profile)
  readonly left: number;
  readonly top: number;
  readonly size: number; // square box side in stage units (= scale * MONKEY_STAGE)
  /** Pivot for the part's own animation (arm rotate / lid scaleY), in source-image units. */
  readonly pivot?: MonkeyPivot;
}

const box = (scale: number): number => scale * MONKEY_STAGE;

/**
 * Layer order (back → front): body, arm (behind head), head, left lid, right lid.
 * Values from the original mascot rig (part transforms + lid pivots).
 */
export const MONKEY_LAYOUT = {
  body: {
    src: require('../../../assets/AnimatedMonkey/body.png'),
    cdn: getCdnUrlExactKey(ASSET_PATHS.ANIMATED_MONKEY.BODY),
    left: 40,
    top: 96,
    size: box(0.94),
  },
  arm: {
    src: require('../../../assets/AnimatedMonkey/arm.png'),
    cdn: getCdnUrlExactKey(ASSET_PATHS.ANIMATED_MONKEY.ARM),
    left: 65,
    top: 25,
    size: box(0.54),
    pivot: { x: 615, y: 690 },
  },
  head: {
    src: require('../../../assets/AnimatedMonkey/head.png'),
    cdn: getCdnUrlExactKey(ASSET_PATHS.ANIMATED_MONKEY.HEAD),
    left: 192,
    top: -40,
    size: box(0.6),
  },
  // Lids are shrunk to 0.83 of their original rect (the full-size domes read as beige blobs
  // larger than the eyes when closed). Each is scaled about its hinge (the transform-origin),
  // so left/top are nudged in by (1 − scale) × origin to keep the crease fixed over the eye.
  // Origin percentages are unchanged (they're relative to the box). Width is narrowed further
  // at render time by LID_BLINK_SCALE_X (about the hinge) — the domes were still too wide.
  lidL: {
    src: require('../../../assets/AnimatedMonkey/lidL.png'),
    cdn: getCdnUrlExactKey(ASSET_PATHS.ANIMATED_MONKEY.LID_L),
    left: 362, // nudged left to sit over the left eye
    top: 72,
    size: box(0.3608 * 0.83),
    pivot: { x: 354, y: 393 },
  },
  lidR: {
    src: require('../../../assets/AnimatedMonkey/lidR.png'),
    cdn: getCdnUrlExactKey(ASSET_PATHS.ANIMATED_MONKEY.LID_R),
    left: 372,
    top: 66,
    size: box(0.371 * 0.83),
    pivot: { x: 692, y: 393 },
  },
} as const satisfies Record<string, MonkeyPartLayout>;

/** Breathe pivot: bottom-centre of the body (512, 980 in stage units). */
export const BREATHE_PIVOT: MonkeyPivot = { x: 512, y: 980 };

/** Head-sway pivot: the neck join (bottom-centre of the head) so the head rocks, not spins. */
export const HEAD_PIVOT: MonkeyPivot = { x: 500, y: 420 };

/** Arm resting angle (degrees) — the pose both the idle state and the wave settle to. */
export const ARM_REST_DEG = '-12deg';

/** Horizontal squish applied to the closed lids (about the hinge) so they match the eye width. */
export const LID_BLINK_SCALE_X = 0.85;

/**
 * Animation timings (ms). Idle motions loop forever via Animated loops. Durations are
 * deliberately non-harmonic so the motions drift out of phase (nothing beats on the "1").
 */
export const MONKEY_DURATION = {
  breatheHalf: 1500,
  wave: 1500,
} as const;

/** Pause (ms) between wave bursts so the continuous wave reads as friendly, not frantic. */
export const WAVE_GAP_MS = 900;

/** One head-sway cycle (ms). Slow and shallow — a subtle secondary motion, not a bobble. */
export const SWAY_PERIOD_MS = 4300;

/**
 * Blink: eyes rest open (lid scaleY 0); a blink drops the lid to fully closed and back.
 * Intervals vary and one entry is a double-blink, so blinking never reads as a metronome.
 * The whole schedule loops.
 */
export const BLINK_TIMING = {
  close: 110,
  open: 150,
  doubleGap: 150,
  closed: 1.15, // >1 stretches the lid down past the box so it covers the eye fully (no bottom peek)
} as const;
export const BLINK_SCHEDULE = [
  { wait: 2400, double: false },
  { wait: 3600, double: false },
  { wait: 2900, double: true },
  { wait: 4200, double: false },
] as const;

/**
 * Keyframes expressed as Animated interpolation stops (driver 0→1 over the cycle).
 */

/** Breathe driver 0→1 maps to group scaleY 1→1.02 (the sequence plays it up then back down). */
export const BREATHE_KEYFRAMES = {
  input: [0, 1],
  output: [1, 1.02],
} as const;

/** Head sway driver 0→1 (one loop) maps to a full sinusoidal rock: 0 → +r → 0 → −r → 0. */
export const SWAY_ROT_KEYFRAMES = {
  input: [0, 0.25, 0.5, 0.75, 1],
  output: ['0deg', '1.4deg', '0deg', '-1.4deg', '0deg'],
} as const;
/** Head sway vertical bob (stage px), lagged a quarter-phase from the rotation. */
export const SWAY_Y_KEYFRAMES = {
  input: [0, 0.25, 0.5, 0.75, 1],
  output: [0, -5, 0, 5, 0],
} as const;

/** One wave burst; starts and ends at the resting angle so it blends back to idle. */
export const WAVE_KEYFRAMES = {
  input: [0, 0.15, 0.3, 0.45, 0.6, 0.75, 1],
  output: [ARM_REST_DEG, '-26deg', '-9deg', '-26deg', '-9deg', '-24deg', ARM_REST_DEG],
} as const;

/** During a wave burst the head leans into it (toward the arm), then returns. */
export const WAVE_TILT_KEYFRAMES = {
  input: [0, 0.5, 1],
  output: ['0deg', '-1.8deg', '0deg'],
} as const;

/** Default rendered size (square, px) — tuned for the Welcome hero. */
export const ANIMATED_MONKEY_DEFAULT_SIZE = 220;

export const ANIMATED_MONKEY_TEST_ID = 'animated-monkey';
